/**
 * Modal for picking a layout when adding a slide.
 *
 * Renders the templates from ``slideTemplates`` as a vertical list with
 * a label + description per row. Click a row → ``onPick(template)``.
 * Escape / backdrop click → ``onClose()``.
 *
 * Kept deliberately framework-light — no portal library, no animation —
 * so it matches the rest of the editor's plain-CSS aesthetic. The
 * backdrop is a fixed full-screen ``<div>`` and the dialog itself is
 * keyboard-navigable.
 */

import { useEffect, useRef } from "react";

import { SLIDE_TEMPLATES, type SlideTemplate } from "../slideTemplates";

interface LayoutPickerModalProps {
  open: boolean;
  onPick(template: SlideTemplate): void;
  onClose(): void;
}

export function LayoutPickerModal({
  open,
  onPick,
  onClose,
}: LayoutPickerModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Close on Escape; focus the first option on open. Two effects so they
  // can run independently — Escape is wired on every open/close cycle,
  // focus only fires on the open transition.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector<HTMLButtonElement>(
        "[data-testid^='layout-picker-option-']",
      );
      firstButton?.focus();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={backdropStyle}
      onClick={onClose}
      data-testid="layout-picker-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Pick a slide layout"
        style={dialogStyle}
        // Click inside the dialog should NOT bubble back up to the
        // backdrop's onClose handler.
        onClick={(e) => e.stopPropagation()}
        data-testid="layout-picker-dialog"
      >
        <header style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: "1.05em" }}>Add a slide</h2>
          <button
            type="button"
            onClick={onClose}
            style={closeStyle}
            aria-label="Close"
            data-testid="layout-picker-close"
          >
            ×
          </button>
        </header>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {SLIDE_TEMPLATES.map((template) => (
            <li key={template.kind}>
              <button
                type="button"
                onClick={() => onPick(template)}
                style={optionStyle}
                data-testid={`layout-picker-option-${template.kind}`}
              >
                <span style={optionLabelStyle}>{template.label}</span>
                <span style={optionDescStyle}>{template.description}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  background: "var(--gray-1)",
  color: "var(--gray-12)",
  border: "1px solid var(--gray-6)",
  borderRadius: 6,
  width: "min(540px, 92vw)",
  maxHeight: "82vh",
  overflowY: "auto",
  boxShadow: "0 24px 56px rgba(0, 0, 0, 0.45)",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.85rem 1rem",
  borderBottom: "1px solid var(--gray-5)",
};

const closeStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--gray-10)",
  fontSize: "1.5em",
  lineHeight: 1,
  cursor: "pointer",
  padding: "0 0.4em",
};

const optionStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "0.15rem",
  padding: "0.7rem 1rem",
  background: "transparent",
  border: "none",
  borderBottom: "1px solid var(--gray-4)",
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
  color: "inherit",
};

const optionLabelStyle: React.CSSProperties = {
  fontSize: "0.95em",
  fontWeight: 600,
};

const optionDescStyle: React.CSSProperties = {
  fontSize: "0.8em",
  color: "var(--gray-10)",
  lineHeight: 1.4,
};
